// What each command is bound to, and the matching that decides whether an event
// counts as that command.
//
// A binding is a chord: modifiers, plus at most one ordinary key held alongside
// them. Every command here is given by holding something and then clicking (or
// pressing a digit), so a binding has to be answerable from a *mouse* event as
// well as a key event. Mouse events report the modifiers but know nothing about
// letters, which is why the ordinary key is matched against a set of currently
// held keys that the game tracks for itself.
import { useState } from "react";

// Ctrl and cmd are treated as one modifier throughout. They are the same key to
// a player — the binding that reads ⌘ on a Mac reads Ctrl everywhere else — and
// the game has always accepted either.
const isApple =
  typeof window !== "undefined" &&
  /Mac|iPhone|iPad|iPod/.test(window.navigator?.platform ?? "");

const COMMAND_LABEL = isApple ? "⌘" : "Ctrl";
const ALT_LABEL = isApple ? "⌥" : "Alt";

// Modifiers arrive as flags on the event, so a chord never stores them as its
// ordinary key — otherwise "Shift" would record as the key Shift with no
// modifiers, and nothing would ever match it.
export const MODIFIER_CODES = new Set([
  "ShiftLeft",
  "ShiftRight",
  "ControlLeft",
  "ControlRight",
  "AltLeft",
  "AltRight",
  "MetaLeft",
  "MetaRight",
]);

// The commands a player can rebind, in the order they are listed in the editor.
// `trigger` is what the chord is held *for*; two commands only shadow each
// other if they share one.
export const COMMANDS = [
  {
    id: "fire",
    label: "Shoot",
    trigger: "left click",
    hint: "Bombard the point you click.",
  },
  {
    id: "move",
    label: "Move",
    trigger: "left click",
    hint: "Order the selection to a point without using the right button.",
  },
  {
    id: "queueMove",
    label: "Queue movement",
    trigger: "right click",
    hint: "Add the point to the end of the route instead of replacing it.",
  },
  {
    id: "setControlGroup",
    label: "Set control group",
    trigger: "0–9",
    hint: "Bind the selection to that digit. The digit alone recalls it.",
  },
];

export const DEFAULT_BINDINGS = {
  fire: { code: "KeyF", command: false, alt: false, shift: false },
  move: { code: null, command: false, alt: false, shift: true },
  queueMove: { code: null, command: false, alt: false, shift: true },
  setControlGroup: { code: null, command: true, alt: false, shift: true },
};

// A chord with nothing in it would fire on every plain click, which would make
// selection impossible. It is never stored and never matches.
export const isEmptyBinding = (binding) =>
  !binding.code && !binding.command && !binding.alt && !binding.shift;

export const sameBinding = (a, b) =>
  a.code === b.code &&
  a.command === b.command &&
  a.alt === b.alt &&
  a.shift === b.shift;

// Exact match, not "at least these": holding shift while a shift-less binding
// is down means the player asked for the shift binding, and only one of the two
// should answer.
const modifiersMatch = (binding, event) =>
  binding.command === (event.ctrlKey || event.metaKey) &&
  binding.alt === event.altKey &&
  binding.shift === event.shiftKey;

// Whether `event` was given with this binding held. `heldKeys` is the set of
// ordinary key codes currently down, tracked by the game because a mouse event
// cannot report them.
export const bindingHeld = (binding, event, heldKeys) => {
  if (!binding || isEmptyBinding(binding)) {
    return false;
  }
  return (
    modifiersMatch(binding, event) &&
    (binding.code === null || heldKeys.has(binding.code))
  );
};

// The chord a key press describes, for the editor's recorder.
export const bindingFromEvent = (event) => ({
  code: MODIFIER_CODES.has(event.code) ? null : event.code || null,
  command: event.ctrlKey || event.metaKey,
  alt: event.altKey,
  shift: event.shiftKey,
});

const KEY_LABELS = {
  Space: "Space",
  Enter: "Enter",
  Tab: "Tab",
  Backspace: "Backspace",
  CapsLock: "Caps",
  Backquote: "`",
  Minus: "-",
  Equal: "=",
  BracketLeft: "[",
  BracketRight: "]",
  Backslash: "\\",
  Semicolon: ";",
  Quote: "'",
  Comma: ",",
  Period: ".",
  Slash: "/",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
};

// Physical keys are stored as codes so a binding stays on the same key whatever
// the layout; the label is what that code looks like on a US keyboard.
export const keyLabel = (code) => {
  if (!code) {
    return "";
  }
  if (KEY_LABELS[code]) {
    return KEY_LABELS[code];
  }
  const letter = /^Key([A-Z])$/.exec(code);
  if (letter) {
    return letter[1];
  }
  const digit = /^(?:Digit|Numpad)([0-9])$/.exec(code);
  if (digit) {
    return digit[1];
  }
  return code;
};

export const formatBinding = (binding) => {
  if (!binding || isEmptyBinding(binding)) {
    return "Unbound";
  }
  const parts = [];
  if (binding.command) {
    parts.push(COMMAND_LABEL);
  }
  if (binding.alt) {
    parts.push(ALT_LABEL);
  }
  if (binding.shift) {
    parts.push("Shift");
  }
  if (binding.code) {
    parts.push(keyLabel(binding.code));
  }
  return parts.join(" + ");
};

const STORAGE_KEY = "bombastic.keyBindings.v1";

// Anything unrecognised is dropped rather than trusted: this comes back from
// localStorage, which an older build or another tab may have written.
const validBinding = (value) =>
  value !== null &&
  typeof value === "object" &&
  (value.code === null || typeof value.code === "string") &&
  typeof value.command === "boolean" &&
  typeof value.alt === "boolean" &&
  typeof value.shift === "boolean" &&
  !isEmptyBinding(value);

const loadBindings = () => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");
    const bindings = { ...DEFAULT_BINDINGS };
    for (const { id } of COMMANDS) {
      if (validBinding(stored[id])) {
        bindings[id] = stored[id];
      }
    }
    return bindings;
  } catch {
    // Private mode, a disabled store, or corrupt JSON. Defaults still play.
    return DEFAULT_BINDINGS;
  }
};

const saveBindings = (bindings) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(bindings));
  } catch {
    // Losing the customisation on refresh beats taking the game down with it.
  }
};

// The bindings in force, and the two ways to change them. Held in state rather
// than a ref so the editor and the on-screen hints redraw when a key changes.
export const useKeyBindings = () => {
  // Passed lazily: useState keeps only the first value, and this component
  // re-renders on every tick, so calling it would re-read storage each time.
  const [bindings, setBindings] = useState(loadBindings);

  const rebind = (id, binding) => {
    if (isEmptyBinding(binding)) {
      return;
    }
    setBindings((current) => {
      const next = { ...current, [id]: binding };
      saveBindings(next);
      return next;
    });
  };

  const resetBindings = () => {
    saveBindings(DEFAULT_BINDINGS);
    setBindings(DEFAULT_BINDINGS);
  };

  return { bindings, rebind, resetBindings };
};

// Commands that answer to the same chord on the same trigger. The first one
// checked wins and the other is unreachable, so the editor says so rather than
// letting a player wonder why a key stopped working.
export const conflictingCommands = (bindings) => {
  const conflicts = {};
  for (const command of COMMANDS) {
    const clashes = COMMANDS.filter(
      (other) =>
        other.id !== command.id &&
        other.trigger === command.trigger &&
        sameBinding(bindings[other.id], bindings[command.id])
    );
    if (clashes.length > 0) {
      conflicts[command.id] = clashes.map((clash) => clash.label);
    }
  }
  return conflicts;
};
